import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { type FormikProps, withFormik } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { withAutosave, type AutosaveProps } from "utils/withAutosave";
import { type EmailTemplateProps, type EmailTemplateFormFields } from "./types";
import { campaignsStateSelector, emailTemplateFormFieldsSelector } from "../state/selectors";
import { type EmailTemplatePayload } from "../types/models";
import { bindAction } from "interfaces/redux";
import { saveEmailTemplate } from "../state/slices/emailTemplateSlice";
import validationSchemas from "utils/validationSchemas";
import { noop } from "lodash";
import { useEffect, useRef } from "react";
import EmailTemplateForm from "./Forms/EmailTemplateForm";
import { createUpdateEmailTemplateAction } from "../state/thunks/campaignEntityStateThunk";
import { Button } from "components/buttons/button/Button";
import Restricted from "features/Application/Restricted";
import { RolePermissions } from "enums";
import RestrictedByTooltip from "components/restrictedByTooltip/RestrictedByTooltip";
import { Tab } from "semantic-ui-react";
import { createTestCampaignSend } from "../state/thunks/campaignSendThunk";
import styles from "./emailTemplate.module.scss";
import { Tooltip } from "components/common/tooltip";

export type EmailTemplatePropsAll = EmailTemplateProps &
  FormikProps<EmailTemplateFormFields> &
  PropsFromRedux &
  AutosaveProps;

const EmailTemplate = (props: EmailTemplatePropsAll) => {
  const { createTestSend } = props;
  const values = useRef(props.values);
  useEffect(() => {
    values.current = props.values;
  }, [props.values]);

  const saveEmailTemplate = () => props.saveEmailTemplate(values.current);

  useEffect(() => {
    props.acceptHandlers?.({
      onNext: saveEmailTemplate,
      onPrevious: saveEmailTemplate,
    });
    props.subscribeOnDiscarded?.(props.resetForm);
    return () => {
      props.unSubscribeOnDiscarded?.(props.resetForm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const panes = [
    {
      menuItem: "Edit",
      pane: {
        key: "edit-template",
        content: <EmailTemplateForm {...props} isLoaded={props.isContentLoaded} id={props.id} />,
      },
    },
  ];

  return (
    <>
      {props.isEdit ? (
        <Tab panes={panes} menu={{ secondary: true, pointing: true }} renderActiveOnly={false} />
      ) : (
        <EmailTemplateForm {...props} isLoaded={props.isContentLoaded} id={props.id} />
      )}
      {!props.isMobo && props.isEdit && (
        <div className={styles["send-test-button"]}>
          <Restricted
            permissions={[RolePermissions.CampaignsManage]}
            renderContent={(hasPermission) => (
              <RestrictedByTooltip hasPermission={hasPermission}>
                <Tooltip
                  target={
                    <Button onClick={() => createTestSend(props.id)} primary fluid disabled={!props.hasBeenPublished}>
                      <span>Send Test</span>
                    </Button>
                  }
                  content={"To send test Phishing Campaign, it must be published."}
                  position="left center"
                  styles={{ width: "17rem" }}
                  hideOnScroll
                  showAlways={hasPermission && !props.hasBeenPublished}
                />
              </RestrictedByTooltip>
            )}
          />
        </div>
      )}
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const base = campaignsStateSelector(state).base;

  return {
    id: base.entityState.entityId,
    isCreating: false,
    emailTemplateFormFields: emailTemplateFormFieldsSelector(state),
    isLoaded: base.emailTemplate.isLoaded,
    isContentLoaded: base.emailTemplate.isContentLoaded,
    isLoading: base.emailTemplate.isLoading,
    isDraft: base.details.campaign.isDraft,
    validation: base.campaignValidation,
    hasBeenPublished: base.details.campaign.hasBeenPublished,
    isMobo: state.userProfile.isMobo,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateEmailTemplate: bindAction(createUpdateEmailTemplateAction(), dispatch),
  saveEmailTemplate: bindAction(saveEmailTemplate, dispatch),
  createTestSend: bindAction(createTestCampaignSend, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

/* istanbul ignore next */
const withAutosaveComponent = withAutosave<EmailTemplatePropsAll, EmailTemplateFormFields, EmailTemplatePayload>({
  getInitValues: (props) => props.emailTemplateFormFields,
  stateProvider: (values) => values,
  entityUpdater: (props) => props.updateEmailTemplate,
  isStateLoaded: (_, props) => props.isLoaded && props.isContentLoaded,
})(EmailTemplate);

/* istanbul ignore next */
const component = withFormik({
  validationSchema: validationSchemas.campaignEmailTemplate,
  enableReinitialize: true,
  mapPropsToValues: (props: EmailTemplateProps & PropsFromRedux): EmailTemplateFormFields =>
    props.emailTemplateFormFields,
  validateOnMount: true,
  handleSubmit: noop,
})(withAutosaveComponent);

const ConnectedComponent = connector(component);
export default ConnectedComponent;
